import { useCallback, useRef } from 'react';

/**
 * Which edge of the panel the resize handle sits on.
 * Controls how drag direction maps to width change:
 * - `'left'`  — handle on left edge (right-side panel): drag left = wider
 * - `'right'` — handle on right edge (left-side panel): drag right = wider
 */
type ResizeDirection = 'left' | 'right';

interface UseResizePxProps {
  /** Current width in pixels (controlled) */
  width: number;
  /** Callback to update width */
  onWidthChange: (width: number) => void;
  /** Minimum width in pixels */
  minWidth: number;
  /** Maximum width in pixels */
  maxWidth: number;
  /** Which edge the resize handle is on. Defaults to `'left'` (right-side panel). */
  direction?: ResizeDirection;
}

interface UseResizePxReturn {
  /**
   * Callback ref to attach to the resize handle element.
   *
   * Implemented as a callback (not a `RefObject`) so the pointer listener is
   * (re)bound every time the handle node mounts and detached when it unmounts.
   * This matters in push mode, where the handle is conditionally rendered
   * (`resizable && open`): a `RefObject` + one-shot effect would bind the
   * listener once and leave it stranded on the removed node after a
   * close/reopen cycle, so the reopened handle would be inert.
   */
  resizeRef: (node: HTMLDivElement | null) => void;
}

/**
 * Pixel-based resize hook for controlled panel widths.
 * Uses native pointer events — no external gesture library required.
 *
 * All mutable values are stored in refs to avoid stale closures, so the
 * callback ref itself is stable and only fires on mount/unmount of the handle.
 */
export function useResizePx({
  width,
  onWidthChange,
  minWidth,
  maxWidth,
  direction = 'left',
}: UseResizePxProps): UseResizePxReturn {
  // Keep everything in refs so the pointerdown listener never needs to change.
  const latest = useRef({ width, onWidthChange, minWidth, maxWidth, direction });
  latest.current = { width, onWidthChange, minWidth, maxWidth, direction };

  const startX = useRef(0);
  const startWidth = useRef(0);

  // Track active drag handlers so we can clean them up if the handle is
  // removed mid-drag (e.g., panel closed while the user is dragging).
  const dragHandlers = useRef<{ move: (e: PointerEvent) => void; up: () => void } | null>(null);

  // The node currently wired up, plus its bound listener, so we can detach
  // cleanly when React swaps or removes the handle element.
  const attached = useRef<{ el: HTMLDivElement; onPointerDown: (e: PointerEvent) => void } | null>(
    null,
  );

  // Tear down any in-flight drag (document-level listeners + body styles).
  const cancelDrag = useCallback(() => {
    if (dragHandlers.current) {
      document.removeEventListener('pointermove', dragHandlers.current.move);
      document.removeEventListener('pointerup', dragHandlers.current.up);
      dragHandlers.current = null;
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Stable callback ref: React invokes it with the node on mount and with null
  // on unmount, so the listener is rebound on every mount of the handle.
  const resizeRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Detach from the previously attached node, if any.
      if (attached.current) {
        attached.current.el.removeEventListener('pointerdown', attached.current.onPointerDown);
        attached.current = null;
        // If the handle vanished mid-drag, abort the drag too.
        cancelDrag();
      }

      if (!node) return;

      function onPointerDown(e: PointerEvent) {
        if (e.button !== 0) return;
        e.preventDefault();

        const { width: w, direction: dir } = latest.current;
        startX.current = e.clientX;
        startWidth.current = w;

        const sign = dir === 'left' ? -1 : 1;

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        function onPointerMove(ev: PointerEvent) {
          const dx = ev.clientX - startX.current;
          const { minWidth: min, maxWidth: max, onWidthChange: onChange } = latest.current;
          const newWidth = Math.round(Math.min(max, Math.max(min, startWidth.current + sign * dx)));
          onChange(newWidth);
        }

        function onPointerUp() {
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
          document.removeEventListener('pointermove', onPointerMove);
          document.removeEventListener('pointerup', onPointerUp);
          dragHandlers.current = null;
        }

        dragHandlers.current = { move: onPointerMove, up: onPointerUp };
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
      }

      node.addEventListener('pointerdown', onPointerDown);
      attached.current = { el: node, onPointerDown };
    },
    [cancelDrag],
  );

  return { resizeRef };
}
